import { useState, type ReactNode } from 'react'

import { cn } from '../../lib/utils'
import { Icon } from '../icon'
import { Button } from '../ui/button'
import { Skeleton } from '../ui/panel'

/**
 * The list screen scaffold.
 *
 * Every list in the console is this shape, and the spacing is the design's to the pixel:
 *
 *   title block   padding: 22px 20px 0
 *   toolbar       padding: 16px 20px 12px
 *   notices       margin:  0 20px 12px
 *   table card    margin:  0 20px 20px, panel over a line border, 9px radius
 *
 * Having it in one place is the point. The previous attempt at this console gave each screen its
 * own padding and the result was four different left edges — the thing the owner objected to
 * first. One scaffold means a new screen cannot invent its own rhythm.
 */

export interface SavedView {
  id: string
  name: string
  by: string
  isDefault?: boolean
}

export interface ViewTab {
  id: string
  label: string
  count?: number
}

export function ListScreen({
  title,
  subtitle,
  primary,
  secondary,
  tabs,
  activeTab,
  onTab,
  savedViews,
  query,
  onQuery,
  queryPlaceholder = 'Filter rows',
  filters,
  notice,
  hint,
  selection,
  children,
}: {
  title: ReactNode
  subtitle?: ReactNode
  primary?: ReactNode
  secondary?: ReactNode
  tabs?: ViewTab[]
  activeTab?: string
  onTab?: (id: string) => void
  savedViews?: SavedView[]
  query?: string
  onQuery?: (value: string) => void
  queryPlaceholder?: string
  /** Filter chips plus the "+ Condition" affordance, where a screen has them. */
  filters?: ReactNode
  /** A full-width warning above the table — a deleted view, a throttle, a degradation. */
  notice?: ReactNode
  hint?: ReactNode
  /** The bulk bar, shown inside the table card when rows are selected. */
  selection?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="min-h-full">
      <div className="flex flex-wrap items-start gap-4 px-5 pt-[22px]">
        <div className="min-w-0">
          <h1 className="text-text m-0 mb-[5px] text-[20px] font-semibold tracking-[-0.028em]">
            {title}
          </h1>
          {subtitle && <p className="text-muted m-0 text-[13px] text-pretty">{subtitle}</p>}
        </div>
        {(primary || secondary) && (
          <div className="ml-auto flex shrink-0 gap-2">
            {secondary}
            {primary}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 px-5 pt-4 pb-3">
        {tabs && tabs.length > 0 && (
          <div className="bg-panel2 border-line flex rounded-[7px] border p-[2px]">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => onTab?.(tab.id)}
                className={cn(
                  'rounded-[5px] px-2.5 py-[3px] text-[12px] transition-colors',
                  tab.id === activeTab
                    ? 'bg-panel text-text font-medium'
                    : 'text-muted hover:text-text',
                )}
              >
                {tab.label}
                {tab.count !== undefined && (
                  <span className="text-faint ml-1.5">{tab.count.toLocaleString()}</span>
                )}
              </button>
            ))}
          </div>
        )}

        {savedViews && <SavedViewsMenu views={savedViews} />}

        {onQuery && (
          <div className="bg-panel border-line focus-within:border-accent flex max-w-[240px] min-w-[140px] flex-1 items-center gap-[7px] rounded-[7px] border px-[9px] py-[5px] transition-colors">
            <Icon name="search" size={12} className="text-faint" />
            <input
              value={query ?? ''}
              onChange={(e) => onQuery(e.target.value)}
              placeholder={queryPlaceholder}
              aria-label={queryPlaceholder}
              className="text-text min-w-0 flex-1 border-0 bg-transparent text-[12px] outline-none"
            />
          </div>
        )}

        {filters}
      </div>

      {notice && <div className="mx-5 mb-3">{notice}</div>}
      {hint && <div className="text-faint mx-5 mb-3 text-[11.5px]">{hint}</div>}

      <div className="bg-panel border-line relative mx-5 mb-5 overflow-hidden rounded-[9px] border">
        {selection}
        {children}
      </div>
    </div>
  )
}

/**
 * A notice above the table.
 *
 * The design uses this for a view someone else deleted, and for a throttle the system applied. It
 * is a coloured band with a border rather than a chip, because these are things that changed under
 * the operator without them asking.
 */
export function Notice({
  tone = 'warn',
  label,
  children,
  actions,
  onDismiss,
}: {
  tone?: 'warn' | 'bad' | 'info'
  /** A short uppercase tag — the one place the design shouts, and only on a system state. */
  label?: string
  children: ReactNode
  actions?: ReactNode
  onDismiss?: () => void
}) {
  const skin =
    tone === 'bad'
      ? 'bg-badbg border-bad text-bad'
      : tone === 'info'
        ? 'bg-infobg border-info text-info'
        : 'bg-warnbg border-warn text-warn'

  return (
    <div
      className={cn('flex items-start gap-2.5 rounded-[8px] border px-3 py-[11px] text-[12.5px]', skin)}
    >
      {label ? (
        <span className="mt-px shrink-0 rounded-[3px] border border-current px-[5px] py-px text-[10px] tracking-[0.06em] uppercase">
          {label}
        </span>
      ) : (
        <Icon
          name={tone === 'bad' ? 'error' : tone === 'info' ? 'info' : 'warning'}
          size={15}
          className="mt-px shrink-0"
        />
      )}
      <span className="min-w-0 flex-1 text-pretty">{children}</span>
      {(actions || onDismiss) && (
        <span className="ml-auto flex shrink-0 gap-[7px]">
          {actions}
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="px-1.5 py-1 text-[12px] opacity-70 transition-opacity hover:opacity-100"
            >
              Dismiss
            </button>
          )}
        </span>
      )}
    </div>
  )
}

/** The bulk-action bar. Accent-soft, inside the table card, above the header row. */
export function BulkBar({
  count,
  onClear,
  children,
}: {
  count: number
  onClear: () => void
  children?: ReactNode
}) {
  if (count === 0) return null

  return (
    <div className="bg-accent-soft border-accent-line text-accent animate-fade flex items-center gap-2.5 border-b px-[14px] py-2 text-[12.5px]">
      <span className="font-medium">{count.toLocaleString()} selected</span>
      <div className="bg-accent-line h-4 w-px" />
      {children}
      <button onClick={onClear} className="ml-auto text-[12px] opacity-75 hover:opacity-100">
        Clear
      </button>
    </div>
  )
}

/** A bulk action. Outlined in the accent line so the bar reads as one control group. */
export function BulkAction({
  children,
  destructive,
  onClick,
}: {
  children: ReactNode
  destructive?: boolean
  onClick?: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-[6px] border px-[9px] py-[3px] text-[12px] transition-colors',
        destructive
          ? 'border-bad text-bad hover:bg-badbg'
          : 'border-accent-line hover:bg-panel text-inherit',
      )}
    >
      {children}
    </button>
  )
}

/** Rows of skeleton, sized like the table they replace. */
export function TableSkeleton({ rows = 8, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="p-[14px]">
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="row-pad flex items-center gap-4">
          <Skeleton className="h-3.5 w-3.5 shrink-0 rounded-[3px]" />
          {Array.from({ length: columns }, (_, column) => (
            <Skeleton
              key={column}
              className="h-3.5 flex-1"
              // Uneven widths, because a grid of identical bars reads as a loading graphic rather
              // than as the shape of the table underneath.
              // eslint-disable-next-line
            />
          ))}
        </div>
      ))}
    </div>
  )
}

/**
 * Saved views.
 *
 * Built because the design specifies it and the shape is what matters — a shared list, which one
 * is default, and who owns it. There is no endpoint, so it says so rather than pretending to load.
 */
function SavedViewsMenu({ views }: { views: SavedView[] }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="relative flex-none">
      <Button
        size="sm"
        variant="secondary"
        trailingIcon="chevronDown"
        onClick={() => setOpen((o) => !o)}
        className="text-muted"
      >
        Saved views
      </Button>

      {open && (
        <>
          <button
            aria-label="Close"
            tabIndex={-1}
            className="fixed inset-0 z-30"
            onClick={() => setOpen(false)}
          />
          <div className="bg-panel border-line animate-fade absolute top-8 left-0 z-40 w-[268px] rounded-[10px] border p-1.5">
            {views.map((view) => (
              <button
                key={view.id}
                className="hover:bg-hover flex w-full items-center gap-2 rounded-[6px] px-[9px] py-1.5 text-left text-[12.5px]"
              >
                <span className="min-w-0 flex-1 truncate">{view.name}</span>
                {view.isDefault && (
                  <span className="bg-accent-soft text-accent shrink-0 rounded-[4px] px-[5px] py-px text-[10px]">
                    default
                  </span>
                )}
                <span className="text-faint shrink-0 text-[10.5px]">{view.by}</span>
              </button>
            ))}
            <div className="bg-line mx-1 my-[5px] h-px" />
            <p className="text-muted px-[9px] pt-1 pb-1.5 text-[11px] leading-[1.45]">
              Shared views are visible to everyone in the organization. There is no endpoint for
              them yet, so nothing here can be saved.
            </p>
          </div>
        </>
      )}
    </div>
  )
}
